import React, { useState, useEffect } from 'react';
import { LoadingScreen } from '../../components/RealSignal/LoadingScreen';
import { AuthPage } from '../../components/RealSignal/AuthPage';
import { OnboardingPage } from '../../components/RealSignal/OnboardingPage';
import { BillingSuccess } from '../../components/RealSignal/BillingSuccess';
import { BillingCancel } from '../../components/RealSignal/BillingCancel';
import { Dashboard } from '../../components/RealSignal/Dashboard';
import { SavedSearches } from '../../components/RealSignal/SavedSearches';
import { AlertsInbox } from '../../components/RealSignal/AlertsInbox';
import { ForgotPasswordPage } from '../../components/RealSignal/ForgotPasswordPage';
import { ResetPasswordPage } from '../../components/RealSignal/ResetPasswordPage';
import { TermsOfServicePage } from '../../components/RealSignal/TermsOfServicePage';
import { PrivacyPolicyPage } from '../../components/RealSignal/PrivacyPolicyPage';
import { realSignalApi, UserProfile } from '../../services/realsignal-api';

type AppState =
    | 'loading'
    | 'auth'
    | 'onboarding'
    | 'billing-success'
    | 'billing-cancel'
    | 'dashboard'
    | 'saved-searches'
    | 'alerts'
    | 'forgot-password'
    | 'reset-password'
    | 'terms'
    | 'privacy';

const RealSignalApp: React.FC = () => {
    const [appState, setAppState] = useState<AppState>('loading');
    const [billingSessionId, setBillingSessionId] = useState<string>('');
    const [pendingUserId, setPendingUserId] = useState<string>('');
    const [loginError, setLoginError] = useState<string>('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [resetToken, setResetToken] = useState<string>('');
    // Centralized user profile state - single source of truth for all screens
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    // Pending action to complete after login (e.g., star a property)
    const [pendingStarPropertyId, setPendingStarPropertyId] = useState<string | null>(null);

    // Pending action storage key and TTL (5 minutes)
    const PENDING_ACTION_KEY = 'realsignal_pending_star';
    const PENDING_ACTION_TTL_MS = 5 * 60 * 1000;

    // Validate propertyId format (must be alphanumeric with colons/hyphens, reasonable length)
    const isValidPropertyId = (id: string): boolean => {
        if (!id || typeof id !== 'string') return false;
        if (id.length < 1 || id.length > 100) return false;
        // Allow alphanumeric, colons, hyphens, underscores (common in property IDs)
        return /^[a-zA-Z0-9:_-]+$/.test(id);
    };

    // Store pending action in sessionStorage with TTL
    const storePendingAction = (propertyId: string) => {
        if (!isValidPropertyId(propertyId)) {
            console.warn('[PendingAction] Invalid propertyId format, not storing');
            return;
        }
        const action = {
            propertyId,
            expiresAt: Date.now() + PENDING_ACTION_TTL_MS,
        };
        sessionStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action));
        setPendingStarPropertyId(propertyId);
    };

    // Retrieve and validate pending action from sessionStorage
    const retrievePendingAction = (): string | null => {
        try {
            const stored = sessionStorage.getItem(PENDING_ACTION_KEY);
            if (!stored) return null;

            const action = JSON.parse(stored);
            // Check TTL
            if (Date.now() > action.expiresAt) {
                sessionStorage.removeItem(PENDING_ACTION_KEY);
                return null;
            }
            // Validate propertyId format
            if (!isValidPropertyId(action.propertyId)) {
                sessionStorage.removeItem(PENDING_ACTION_KEY);
                return null;
            }
            return action.propertyId;
        } catch {
            sessionStorage.removeItem(PENDING_ACTION_KEY);
            return null;
        }
    };

    // Clear pending action (single-use)
    const clearPendingAction = () => {
        sessionStorage.removeItem(PENDING_ACTION_KEY);
        setPendingStarPropertyId(null);
    };

    // Set realSignal branding (title and favicon) on mount
    useEffect(() => {
        const originalTitle = document.title;
        const originalFavicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.href;

        document.title = 'realSignal';

        const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (favicon) {
            favicon.href = '/realsignal-logo.png';
        }

        return () => {
            document.title = originalTitle;
            const faviconEl = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
            if (faviconEl && originalFavicon) {
                faviconEl.href = originalFavicon;
            }
        };
    }, []);

    // Boot-time auth rehydration and URL routing
    useEffect(() => {
        const path = window.location.pathname;
        const params = new URLSearchParams(window.location.search);

        if (path.includes('/billing/success')) {
            const sessionId = params.get('session_id');
            if (sessionId) {
                setBillingSessionId(sessionId);
                setAppState('billing-success');
                return;
            }
        }

        if (path.includes('/billing/cancel')) {
            setAppState('billing-cancel');
            return;
        }

        // Check for password reset token in URL
        const token = params.get('reset_token');
        if (token) {
            setResetToken(token);
            setAppState('reset-password');
            return;
        }

        // Boot-time auth rehydration - single source of truth
        const rehydrate = async () => {
            console.log('[App Boot] Starting auth rehydration...');

            // Check for pending action from sessionStorage (survives page refresh)
            const pendingId = retrievePendingAction();
            if (pendingId) {
                console.log('[App Boot] Found pending star action:', pendingId);
                setPendingStarPropertyId(pendingId);
            }

            const profile = await realSignalApi.rehydrateAuth();
            if (profile) {
                console.log('[App Boot] Auth rehydrated - user:', profile.email);
                setUserProfile(profile);
                setAppState('dashboard');
            } else {
                console.log('[App Boot] No valid session - showing auth');
            }
        };
        rehydrate();
    }, []);

    // Subscribe to auth events for centralized session management
    useEffect(() => {
        const unsubscribe = realSignalApi.onAuthEvent(event => {
            console.log('[App] Auth event received:', event);
            if (event === 'SESSION_EXPIRED' || event === 'LOGGED_OUT') {
                setUserProfile(null);
                setAppState('auth');
            } else if (event === 'LOGGED_IN') {
                const profile = realSignalApi.getUserProfile();
                if (profile) {
                    setUserProfile(profile);
                }
            }
        });
        return unsubscribe;
    }, []);

    const handleLoadComplete = () => {
        const path = window.location.pathname;
        if (path.includes('/billing/')) {
            return;
        }

        if (realSignalApi.isAuthenticated()) {
            setAppState('dashboard');
        } else {
            setAppState('auth');
        }
    };

    const handleLogin = async (email: string, password: string, rememberMe: boolean = false) => {
        setLoginError('');
        setIsLoggingIn(true);

        try {
            const result = await realSignalApi.login(email, password, rememberMe);

            if (result.state === 'success') {
                // After login, rehydrate to get full user profile
                const profile = await realSignalApi.rehydrateAuth();
                if (profile) {
                    setUserProfile(profile);
                }
                setAppState('dashboard');
            } else {
                setLoginError(result.error || 'Login failed. Please try again.');
            }
        } catch (error) {
            setLoginError('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleLogout = async () => {
        await realSignalApi.logout();
        setUserProfile(null);
        setAppState('auth');
    };

    const handleForgotPassword = async (email: string) => {
        await realSignalApi.requestPasswordReset(email);
        return 'If an account exists with this email, you will receive a password reset link.';
    };

    const handleResetPassword = async (token: string, newPassword: string) => {
        const result = await realSignalApi.resetPassword(token, newPassword);
        if (result.state === 'error') {
            throw new Error(result.error || 'Failed to reset password');
        }
        return result.data?.message || 'Password reset successfully';
    };

    const handleResetPasswordBack = () => {
        // Clear the reset token from URL and state
        window.history.replaceState({}, '', '/realsignal');
        setResetToken('');
        setAppState('auth');
    };

    const handleCreateAccount = async (data: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        betaCode?: string;
    }) => {
        try {
            // Step 1: Create account (pending_payment state)
            const signupResponse = await fetch('/api/realsignal/auth/signup', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            const signupData = await signupResponse.json();

            if (!signupData.success) {
                alert(signupData.error || 'Failed to create account');
                return;
            }

            const userId = signupData.data.userId;
            setPendingUserId(userId);

            // Step 2: Create Stripe checkout session
            const checkoutResponse = await fetch('/api/realsignal/billing/checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId }),
            });

            const checkoutData = await checkoutResponse.json();

            if (!checkoutData.success) {
                alert(checkoutData.error || 'Failed to create checkout session');
                return;
            }

            // Step 3: Redirect to Stripe Checkout
            window.location.href = checkoutData.data.checkoutUrl;
        } catch (error) {
            console.error('Account creation error:', error);
            alert('An error occurred. Please try again.');
        }
    };

    const handleBillingVerified = () => {
        // Clear URL params and redirect to dashboard
        window.history.replaceState({}, '', '/realsignal');
        setAppState('dashboard');
    };

    const handleBillingRetry = async () => {
        // If we have a pending user ID, try to create a new checkout session
        if (pendingUserId) {
            try {
                const checkoutResponse = await fetch('/api/realsignal/billing/checkout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ userId: pendingUserId }),
                });

                const checkoutData = await checkoutResponse.json();

                if (checkoutData.success) {
                    window.location.href = checkoutData.data.checkoutUrl;
                    return;
                }
            } catch (error) {
                console.error('Retry checkout error:', error);
            }
        }

        // Fallback: go back to onboarding
        window.history.replaceState({}, '', '/realsignal');
        setAppState('onboarding');
    };

    if (appState === 'loading') {
        return <LoadingScreen onLoadComplete={handleLoadComplete} minDisplayTime={2500} />;
    }

    if (appState === 'auth') {
        return (
            <AuthPage
                onLogin={handleLogin}
                onGoogleLogin={() => setAppState('dashboard')}
                onForgotPassword={() => setAppState('forgot-password')}
                onActivateAccount={() => setAppState('onboarding')}
                onTermsClick={() => setAppState('terms')}
                onPrivacyClick={() => setAppState('privacy')}
                error={loginError}
                isLoading={isLoggingIn}
            />
        );
    }

    if (appState === 'forgot-password') {
        return (
            <ForgotPasswordPage
                onSubmit={handleForgotPassword}
                onBack={() => setAppState('auth')}
            />
        );
    }

    if (appState === 'reset-password') {
        return (
            <ResetPasswordPage
                token={resetToken}
                onSubmit={handleResetPassword}
                onBack={handleResetPasswordBack}
            />
        );
    }

    if (appState === 'terms') {
        return <TermsOfServicePage onBack={() => setAppState('auth')} />;
    }

    if (appState === 'privacy') {
        return <PrivacyPolicyPage onBack={() => setAppState('auth')} />;
    }

    if (appState === 'onboarding') {
        return (
            <OnboardingPage
                onCreateAccount={handleCreateAccount}
                onGoogleSignUp={() => setAppState('dashboard')}
                onSignIn={() => setAppState('auth')}
            />
        );
    }

    if (appState === 'billing-success') {
        return (
            <BillingSuccess
                sessionId={billingSessionId}
                onVerified={handleBillingVerified}
                onError={error => {
                    console.error('Billing verification error:', error);
                }}
            />
        );
    }

    if (appState === 'billing-cancel') {
        return (
            <BillingCancel
                onRetry={handleBillingRetry}
                onSignIn={() => {
                    window.history.replaceState({}, '', '/realsignal');
                    setAppState('auth');
                }}
            />
        );
    }

    if (appState === 'saved-searches') {
        return <SavedSearches onBack={() => setAppState('dashboard')} />;
    }

    if (appState === 'alerts') {
        return <AlertsInbox onBack={() => setAppState('dashboard')} />;
    }

    return (
        <Dashboard
            onLogout={handleLogout}
            onNavigateToAlerts={() => setAppState('alerts')}
            onNavigateToSavedSearches={() => setAppState('saved-searches')}
            onRequestLogin={(pendingPropertyId?: string) => {
                if (pendingPropertyId) {
                    // Store in sessionStorage with TTL for persistence across page refresh
                    storePendingAction(pendingPropertyId);
                }
                setAppState('auth');
            }}
            userProfile={userProfile}
            pendingStarPropertyId={pendingStarPropertyId}
            onPendingStarComplete={clearPendingAction}
        />
    );
};

export default RealSignalApp;
